import { Box } from "@chakra-ui/react";
import { FC, RefObject, useEffect, useRef } from "react";
import { MdClose } from "react-icons/md";

import { SamedayPickupLocationInput } from "@components/organisms/SamedayPickup/LocationInput";

import { SamedayPickupMessageCard } from "../MessageCard";
import { SamedayPickupStatus, useSamedayPickup } from "../useSamedayPickup";

type SamedayPickupDrawerProps = {
  autocompleteInputRef: RefObject<HTMLInputElement>;
  updateSamedayPickupStatus: (args: SamedayPickupStatus) => void;
  isDrawerOpen: boolean;
  productLocation: string;
  setIsDrawerOpen: (isDrawerOpen: boolean) => void;
};

export const SamedayPickupDrawer: FC<SamedayPickupDrawerProps> = ({
  autocompleteInputRef,
  updateSamedayPickupStatus,
  isDrawerOpen,
  productLocation,
  setIsDrawerOpen,
}) => {
  const {
    canPickupToday,
    distance,
    errorMessage,
    handleUseCurrentLocation,
    isSamedayEligible,
    loading,
    reset,
    suggestions,
    isLoadingSuggestions,
    handlePlaceSelection,
  } = useSamedayPickup({
    productWarehouseLocation: productLocation || "",
    inputElement: autocompleteInputRef.current,
    onStatusChange: updateSamedayPickupStatus,
  });

  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (drawerRef.current) {
        const rect = drawerRef.current.getBoundingClientRect();
        const isInBounds =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom;

        if (!isInBounds) {
          setIsDrawerOpen(false);
        }
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsDrawerOpen(false);
      }
    };

    if (isDrawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isDrawerOpen, setIsDrawerOpen, reset]);

  return (
    <>
      {isDrawerOpen && (
        <Box
          backgroundColor="rgba(0, 0, 0, 0.5)"
          height="100vh"
          position="fixed"
          top={0}
          width="100%"
          zIndex={1000}
        />
      )}
      <Box
        backgroundColor="white"
        boxShadow={isDrawerOpen ? "dark-lg" : "none"}
        height="100%"
        minWidth="24rem"
        position="fixed"
        ref={drawerRef}
        right={0}
        top={0}
        transform={isDrawerOpen ? "translateX(0)" : "translateX(100%)"}
        transition="transform 0.3s"
        zIndex={1000}
      >
        <Box
          alignItems="center"
          borderBottom="2px solid black"
          display="flex"
          justifyContent="space-between"
          padding="1rem"
          position="sticky"
          top="0"
        >
          <h3 style={{ fontSize: "20px", fontWeight: 700, lineHeight: "24px" }}>
            SET YOUR LOCATION
          </h3>
          <MdClose
            cursor="pointer"
            size={24}
            onClick={() => {
              setIsDrawerOpen(false);
            }}
          />
        </Box>
        <Box padding="1rem">
          <SamedayPickupLocationInput
            autocompleteInputRef={autocompleteInputRef}
            handleUseCurrentLocation={handleUseCurrentLocation}
            loading={loading}
            errorMessage={errorMessage}
            reset={reset}
            setIsDrawerOpen={setIsDrawerOpen}
            distance={distance}
            suggestions={suggestions}
            isLoadingSuggestions={isLoadingSuggestions}
            handlePlaceSelection={handlePlaceSelection}
          />
        </Box>
        {!loading && distance && (
          <Box margin="0 1rem">
            <SamedayPickupMessageCard
              isSamedayEligible={isSamedayEligible}
              distance={distance}
              canPickupToday={canPickupToday}
            />
          </Box>
        )}
      </Box>
    </>
  );
};
